import type {
  AggregationType,
  ColumnAggregationMap,
  DateGroupingMap,
} from '../aggregation-types.ts';
import type { ColumnFormatConfig } from '../column-format-options.ts';
import type { SortEntry } from '../sort-helpers.ts';
import type { WidgetConfig, WidgetDimension } from './widget-config-types.ts';

/** Column key of a count-of-documents measure; the server maps it to each bucket's doc_count. */
export const DOCUMENT_COUNT_COLUMN = 'doc_count';

export interface WidgetExportGroupBy {
  selectedFields: string[];
  columnAggregations: ColumnAggregationMap;
  dateGroupingIntervals?: DateGroupingMap;
}

/** A widget's data in the report builder's tabular shape — what the server's CSV pipeline consumes. */
export interface WidgetExportDefinition {
  selectedColumns: string[];
  groupBy?: WidgetExportGroupBy;
  columnLabels?: Record<string, string>;
  columnFormats?: Record<string, ColumnFormatConfig>;
  sorts?: SortEntry[];
  /** True when `groupBy` carries at least one field. */
  isAggregated: boolean;
}

interface ExportMeasure {
  fieldKey: string | null;
  aggregation: AggregationType;
  label?: string;
}

const COUNT_MEASURE: ExportMeasure = { fieldKey: null, aggregation: 'count' };

interface ChartParts {
  dimensions: WidgetDimension[];
  measures: ExportMeasure[];
}

/** A chart's or pie's dimensions and measures; undefined for other kinds and for incomplete widgets. */
function chartParts(widget: WidgetConfig): ChartParts | undefined {
  if (widget.widgetType === 'pie') {
    if (!widget.sliceDimension) return undefined;
    return {
      dimensions: [widget.sliceDimension],
      measures: [widget.valueMeasure ?? COUNT_MEASURE],
    };
  }
  if (widget.widgetType !== 'chart') return undefined;
  const measures = [...(widget.bars ?? []), ...(widget.lines ?? [])];
  if (!widget.xDimension || !measures.length) return undefined;
  const dimensions = widget.groupDimension
    ? [widget.xDimension, widget.groupDimension]
    : [widget.xDimension];
  return { dimensions, measures };
}

/**
 * Why a widget cannot be exported without losing a measure, or undefined when it can.
 * The export holds one aggregation per field, so two different aggregations of one
 * field, or a non-count aggregation of a field the chart groups by, cannot both reach
 * the file. Repeating an identical measure loses nothing and is allowed.
 *
 * @returns a message written for the viewer
 */
export function widgetExportBlocker(widget: WidgetConfig): string | undefined {
  const parts = chartParts(widget);
  if (!parts) return undefined;
  const dimensionKeys = parts.dimensions.map((d) => d.fieldKey);
  const aggregationByField = new Map<string, AggregationType>();
  for (const m of parts.measures) {
    if (!m.fieldKey) continue;
    if (dimensionKeys.includes(m.fieldKey)) {
      if (m.aggregation !== 'count') {
        return 'This chart aggregates a field it also groups by, which the CSV export can’t hold yet.';
      }
      continue;
    }
    const seen = aggregationByField.get(m.fieldKey);
    if (seen && seen !== m.aggregation) {
      return 'This chart measures one field two ways, which the CSV export can’t hold yet.';
    }
    aggregationByField.set(m.fieldKey, m.aggregation);
  }
  return undefined;
}

function emptyDefinition(): WidgetExportDefinition {
  return { selectedColumns: [], isAggregated: false };
}

function tableDefinition(widget: WidgetConfig): WidgetExportDefinition {
  const table = widget.table;
  const groupBy = table?.groupBy;
  return {
    selectedColumns: [...(table?.selectedColumns ?? [])],
    ...(groupBy ? { groupBy } : {}),
    ...(table?.columnLabels ? { columnLabels: table.columnLabels } : {}),
    ...(table?.columnFormats ? { columnFormats: table.columnFormats } : {}),
    ...(table?.sorts ? { sorts: table.sorts } : {}),
    isAggregated: Boolean(groupBy?.selectedFields?.length),
  };
}

function measuresDefinition(
  dimensions: WidgetDimension[],
  measures: ExportMeasure[]
): WidgetExportDefinition {
  const selectedFields = dimensions.map((d) => d.fieldKey);
  const dateGroupingIntervals: DateGroupingMap = {};
  for (const d of dimensions) {
    if (d.dateInterval) dateGroupingIntervals[d.fieldKey] = d.dateInterval;
  }

  const columnAggregations: ColumnAggregationMap = {};
  const columnLabels: Record<string, string> = {};
  const measureColumns: string[] = [];
  for (const m of measures) {
    // Counting a grouped field counts its bucket: every document in the bucket carries the field.
    const countsBucket =
      !m.fieldKey || (m.aggregation === 'count' && selectedFields.includes(m.fieldKey));
    const key = countsBucket ? DOCUMENT_COUNT_COLUMN : (m.fieldKey as string);
    if (selectedFields.includes(key) || measureColumns.includes(key)) continue;
    measureColumns.push(key);
    if (!countsBucket) columnAggregations[key] = m.aggregation;
    if (m.label) columnLabels[key] = m.label;
  }

  return {
    selectedColumns: [...selectedFields, ...measureColumns],
    groupBy: {
      selectedFields,
      columnAggregations,
      ...(Object.keys(dateGroupingIntervals).length ? { dateGroupingIntervals } : {}),
    },
    ...(Object.keys(columnLabels).length ? { columnLabels } : {}),
    isAggregated: selectedFields.length > 0,
  };
}

/**
 * Map a widget to the tabular definition its CSV export runs with.
 *
 * @param widget a table copies its own definition; a chart groups by X then Group, a pie
 *   by the slice. A chart without an X axis or measures, or a pie without a slice, yields no
 *   columns — the inputs the renderer shows empty — and so does every counter: the server has
 *   no single-value export path yet, so counters are not exported today
 * @returns measures as metric columns in bars-then-lines order; a count of documents, or a
 *   count of a dimension's own field, is `DOCUMENT_COUNT_COLUMN` with no aggregation entry,
 *   and identical repeated measures share one column. Layouts that would lose a measure are
 *   for `widgetExportBlocker` to refuse first. Charts carry no `columnFormats` (raw
 *   numbers), and a grouped chart's lines export at the X×Group grain rather than the
 *   chart's X level
 */
export function widgetExportDefinition(widget: WidgetConfig): WidgetExportDefinition {
  switch (widget.widgetType) {
    case 'table':
      return tableDefinition(widget);
    default: {
      const parts = chartParts(widget);
      return parts ? measuresDefinition(parts.dimensions, parts.measures) : emptyDefinition();
    }
  }
}
